import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { LabelPickerMenuComponent } from '../label-picker-menu/label-picker-menu.component';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { AppState } from 'src/app/core/app.state';
import { IAccount } from '../../../models/account.models';
import { IConversation, IConversationCustomer, IConversationDetails, IConversationUpdate, ConversationMenuOptions, IConversationThreadContext, ConversationClassification, ConversationCounterparty, IMessageClassificationUpdate, MESSAGE_INTENTS_BY_CATEGORY, SELECTABLE_CLASSIFICATIONS, SELECTABLE_COUNTERPARTIES, IConversationLabel, IPendingProviderLabel, IThreadLabelUpdate } from '../../../models/conversation.models';
import {
  IConversationTimelineObject,
  ITimelineComment,
  ITimelineMessage,
  ITimelineAttachment,
  isTimelineMessage,
  isTimelineEvent,
  ITimelineEvent,
  isTimelineComment,
  isTimelineOrder,
  isTimelineOrderPlaceholder,
  isTimelineOrderGroup,
  ITimelineOrder,
  TimelineEventSource,
  TimelineViewMode,
} from '../../../models/timeline.models';
import { groupTimelineOrders } from '../../../utils/order-group.util';
import { ConversationService } from '../../../services/conversation.service';
import { ComposeService } from '../../../services/compose.service';
import { DraftService } from '../../../services/draft.service';
import { sanitizeHtml as sanitizeHtmlUtil, getCleanedHtmlString, splitEmailQuote, buildForwardEditorBody, SplitEmailResult } from '../../../utils/email-body-sanitizer.util';
import { getUserInitials, getUserFullname, getUserColor } from '../../../utils/user.util';
import {
  badgedCounterparty,
  classificationLabelKey,
  isClassified,
  confirmNeedsCompany,
  counterpartyConfidencePercent,
  counterpartyLabelKey,
  counterpartySourceLabelKey,
  counterpartyModelReading,
  counterpartyTagVariant,
  hasCounterpartyConflict,
  humanizeIntent,
  intentLabelKey,
  isToVerify,
  orderCategories,
  storedCounterparty,
  toCounterparty,
} from '../../../utils/classification.util';
import { buildMailboxTag, IMailboxTag } from '../../../utils/mailbox-tag.util';
import { MailboxTagColor } from '../../../utils/user.util';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { ConversationActionMenuComponent } from '../conversation-action-menu/conversation-action-menu.component';
import { ConversationDetailSidebarComponent } from '../conversation-detail-sidebar/conversation-detail-sidebar.component';
import { CustomerAssociationMenuComponent } from '../customer-association-menu/customer-association-menu.component';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { CommonService } from 'src/app/shared/common.service';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { InboxConfirmationDialogComponent } from '../../inbox-confirmation-dialog/inbox-confirmation-dialog.component';
import { FilePreviewService } from 'src/app/shared/file-preview-dialog/file-preview.service';
import { DEFAULT_MAILBOX_TAG_DISPLAY, InboxConfirmationActionType, InboxFolderType, MailboxTagDisplay } from '../../../models/inbox.models';
import { MailboxTagPreferenceService } from '../../../services/mailbox-tag-preference.service';
import { FileSizePipe } from 'src/app/config/filesize-pipe';
import { ShortAmountPipe } from 'src/app/config/short-amount.pipe';
import { HighlightPipe } from 'src/app/core/pipes/highlight.pipe';
import { Router } from '@angular/router';
import { SideBySideCorrectionPanelComponent, sideBySideResultMutatedOrder } from 'src/app/features/quotations/components/side-by-side-correction-panel/side-by-side-correction-panel.component';
import { QuotesService } from 'src/app/features/quotations/services/quotations.services';
import { skipErrorToast } from 'src/app/core/http/error-toast.token';
import { OrderArtifactComponent } from '../order-artifact/order-artifact.component';
import { OrderGroupArtifactComponent } from '../order-group-artifact/order-group-artifact.component';
import { TIMELINE_VIEW_OPTIONS } from '../../../data/conversation.data';
import * as smartInboxActions from '../../../store/smart-inbox.actions';
import * as smartInboxSelectors from '../../../store/smart-inbox.selectors';
import { getHumanReadableTimelineEvent, getIconForTimelineEvent, getTimelineActorLabel } from '../../../utils/timeline.util';
import { IUser } from 'src/app/features/settings/models/settings.models';
import { SettingsService } from 'src/app/features/settings/services/settings.services';
import { MentionConfig, MentionModule } from 'angular-mentions';
import { AdaptToUserUtcOffsetPipe } from 'src/app/core/pipes/adapt-to-user-utc-offset';

// ─────────────────────────────────────────────────────────────────────────────
// ConversationDetailComponent
// ─────────────────────────────────────────────────────────────────────────────
// Displays the selected conversation's timeline (messages, events, orders, comments),
// metadata, and action menus.
//
// Responsibilities:
//   • Timeline (messages, events, orders, comments) with expand/collapse and scroll
//   • Reply / reply-all / forward, category / status / assignee / customer updates
//   • Comment create/edit/delete/copy with @mentions
//   • Timeline order row: edit (quotations page), copy, download PDF; create order from header
//   • Bulk actions, attachment downloads, sidebar toggle
// ─────────────────────────────────────────────────────────────────────────────

/** Max recipients shown inline on a message header before collapsing into an "x more" suffix. */
const MAX_DISPLAYED_RECIPIENTS = 3;

/** Max thread categories shown inline in the header before collapsing into "+n". */
const MAX_VISIBLE_THREAD_CATEGORIES = 2;
/**
 * Two, not three: measured against the header with the customer panel open,
 * three chips force the categories chip to ellipsize to "Un...". The rest go
 * behind "+n".
 */
const MAX_VISIBLE_LABELS = 2;
const MAX_VISIBLE_PENDING_LABELS = 1;
/** Order states that hide the order from the timeline entirely. */
const DELETED_ORDER_STATES = new Set(['deleted']);

/** Attachment extensions the shared <app-files-previewer> can render inline (drives the preview affordance). */
const PREVIEWABLE_ATTACHMENT_EXTENSIONS = new Set<string>([
  'pdf',
  'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg',
  'xlsx', 'xls', 'xlsm', 'xlsb', 'csv',
  'txt', 'log', 'md', 'json', 'xml',
  'html', 'htm',
]);

@Component({
  selector: 'app-conversation-detail',
  standalone: true,
  templateUrl: './conversation-detail.component.html',
  styleUrls: ['./conversation-detail.component.scss'],
  imports: [
    // The header's label menu is declared inline here; the assignee menu next
    // to it borrows a child component's, which is why this was never needed before.
    MatMenuModule,
    LabelPickerMenuComponent,
    CommonModule,
    UiModule,
    TranslateModule,
    SharedModule,
    ConversationActionMenuComponent,
    ConversationDetailSidebarComponent,
    CustomerAssociationMenuComponent,
    OrderArtifactComponent,
    OrderGroupArtifactComponent,
    FileSizePipe,
    ShortAmountPipe,
    HighlightPipe,
    MentionModule
  ],
})
export class ConversationDetailComponent implements OnInit, OnDestroy {

  // ── Decorated properties ───────────────────────────────────────────────────

  @ViewChild(ConversationActionMenuComponent, { static: true }) conversationActionMenuRef!: ConversationActionMenuComponent;
  @ViewChild(CustomerAssociationMenuComponent) customerAssociationMenuRef!: CustomerAssociationMenuComponent;
  // Bound by template ref, not by type: an untargeted @ViewChild(MatMenuTrigger)
  // resolves to the first trigger in the template (the status pill), so
  // closeCustomerMenu() was closing the wrong menu and leaving this one open.
  @ViewChild('customerMenuTrigger') customerAssociationMenuTrigger!: MatMenuTrigger;
  @ViewChild('customerAssociationMenuTriggerBulk') customerAssociationMenuTriggerBulk!: MatMenuTrigger;
  @ViewChild('confirmCustomerMenu') confirmCustomerMenu?: MatMenu;
  @ViewChildren('messageItem') messageItemRefs!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('timelineContainer') timelineContainerRef!: ElementRef<HTMLElement>;
  @ViewChild('commentInput') commentInputRef!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('commentEditInput') commentEditInputRef!: ElementRef<HTMLTextAreaElement>;

  // ── Public properties (from store) ─────────────────────────────────────────

  activeConversation: IConversation | null = null;
  activeConversationDetails: IConversationDetails | null = null;
  activeConversationThreadContext: IConversationThreadContext | null = null;
  conversationDetailsLoading = false;
  isDeepLinkLoading = false;
  selectedConversationIds: number[] = [];
  bulkMarkAsRead = true;
  accounts: IAccount[] = [];
  /** Show the recipient-mailbox tag on every thread (2+ mailboxes connected). */
  mailboxTagAlwaysVisible = false;
  /** Tint per mailbox id, unique across the connected accounts. */
  mailboxTagColors: Record<number, MailboxTagColor> = {};
  /** The user's Settings choice for where the tag may appear. */
  mailboxTagDisplay: MailboxTagDisplay = DEFAULT_MAILBOX_TAG_DISPLAY;
  activeFolder: InboxFolderType = InboxFolderType.ALL;

  // ── Public properties (local state) ────────────────────────────────────────

  activeConversationMenu: ConversationMenuOptions | null = null;
  customerMenuIsOpen = false;
  /** Message whose `customer` confirmation waits for a company; non-null opens the picker. */
  pendingCustomerConfirmation: ITimelineMessage | null = null;
  conversationUpdateType: 'single' | 'bulk' = 'single';
  updatingCategory = false;
  updatingStatus = false;
  updatingAssignee = false;
  updatingCustomer = false;
  /** Ids of messages whose category/intent override is currently in flight. */
  classifyingMessageIds: number[] = [];
  updatingUrgency = false;
  confirmSharePersonalConversation = false;
  searchTerm = '';
  /** Timeline display filter, driven by the header "View" dropdown (persisted per user). */
  timelineViewMode: TimelineViewMode = TimelineViewMode.FULL;
  sidebarOpen = true;
  /** Whether the inline internal-message (comment) composer is expanded in the action bar. */
  internalComposerOpen = false;
  expandedMessageIds = new Set<number | string>();
  expandedQuoteIds = new Set<number | string>();

  commentInputValue = '';
  commentMentionConfig: MentionConfig = { triggerChar: '@', labelKey: 'name', dropUp: true };
  mentionListOpen = false;
  /** Set when a mention was just selected (e.g. via Enter); prevents that same Enter from sending. */
  mentionJustSelected = false;

  editingCommentId: number | null = null;
  editedCommentBody = '';
  selectedEditMentionIds: number[] = [];
  loadingCommentId: number | null = null;
  currentUser: { id: number; name?: string; email?: string } | null = null;

  // ── Readonly refs ─────────────────────────────────────────────────────────

  readonly CONVERSATION_MENU_OPTIONS = ConversationMenuOptions;
  readonly CONVERSATION_CLASSIFICATION = ConversationClassification;
  /** Categories offered when correcting a message. */
  readonly CLASSIFICATION_OPTIONS = SELECTABLE_CLASSIFICATIONS;
  /** Counterparties offered when correcting a message. */
  readonly COUNTERPARTY_OPTIONS = SELECTABLE_COUNTERPARTIES;
  readonly classificationLabelKey = classificationLabelKey;
  readonly counterpartyLabelKey = counterpartyLabelKey;
  readonly counterpartyTagVariant = counterpartyTagVariant;
  readonly counterpartyConfidencePercent = counterpartyConfidencePercent;
  readonly confirmNeedsCompany = confirmNeedsCompany;
  readonly isToVerify = isToVerify;
  readonly counterpartyModelReading = counterpartyModelReading;
  readonly getUserInitials = getUserInitials;
  readonly getUserFullname = getUserFullname;
  readonly getUserColor = getUserColor;
  readonly isTimelineMessage = isTimelineMessage;
  readonly isTimelineEvent = isTimelineEvent;
  readonly isTimelineComment = isTimelineComment;
  readonly isTimelineOrder = isTimelineOrder;
  readonly isTimelineOrderPlaceholder = isTimelineOrderPlaceholder;
  readonly isTimelineOrderGroup = isTimelineOrderGroup;

  readonly TIMELINE_VIEW_OPTIONS = TIMELINE_VIEW_OPTIONS;
  /** localStorage key persisting the user's timeline "View" preference. */
  private readonly TIMELINE_VIEW_STORAGE_KEY = 'smartInbox.timelineViewMode';

  // ── Private properties ──────────────────────────────────────────────────────

  private users: IUser[] = [];
  /** Message id to scroll-to and highlight after details load (set when opening a sent message). */
  private activeMessageId: number | null = null;
  private selectedMentionIds: number[] = [];
  private previewCache = new Map<string, string>();
  private sanitizedHtmlCache = new Map<string, SafeHtml>();
  private splitCache = new Map<string, SplitEmailResult>();
  /** Keyed on the identities `timelineItems` derives from — see that getter. */
  private timelineItemsCache: {
    details: IConversationDetails | null;
    viewMode: TimelineViewMode;
    items: IConversationTimelineObject[] | null;
  } | null = null;
  private readonly toastOptions = { duration: 3000, verticalPosition: 'bottom' as const, horizontalPosition: 'right' as const };
  private destroy$ = new Subject<void>();

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(
    private sanitizer: DomSanitizer,
    private conversationService: ConversationService,
    private store: Store<AppState>,
    private actions$: Actions,
    private commonService: CommonService,
    private composeService: ComposeService,
    private draftService: DraftService,
    private dialog: MatDialog,
    private authService: AuthService,
    private quotesService: QuotesService,
    private translate: TranslateService,
    private filePreview: FilePreviewService,
    private mailboxTagPreference: MailboxTagPreferenceService,
    private settingsService: SettingsService,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    const authUser = this.authService.getCurrentUser();
    this.currentUser = authUser ? { id: authUser.id, name: authUser.name, email: authUser.email } : null;

    this.restoreTimelineViewMode();

    this.store.select((state) => state.settings.allUsers)
      .pipe(takeUntil(this.destroy$))
      .subscribe((users) => { this.users = users; });

    this.store.select(smartInboxSelectors.selectActiveConversation)
      .pipe(takeUntil(this.destroy$))
      .subscribe((conversation) => { this.activeConversation = conversation; });

    this.store.select(smartInboxSelectors.selectActiveConversationDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe((details) => {
        const prevDetails = this.activeConversationDetails;
        this.activeConversationDetails = details;
        // A decision still in flight belongs to the conversation it was made in.
        if (details?.id !== prevDetails?.id) {
          this.decidingProviderRefId = null;
        }
        this.activeConversationThreadContext = details?.thread_context ?? null;
        if (details && details !== prevDetails) {
          const hasContent = (details.objects?.length ?? 0) > 0;
          if (hasContent) {
            this.previewCache.clear();
            this.sanitizedHtmlCache.clear();
            this.splitCache.clear();
            this.expandedQuoteIds.clear();
            // Do not call onDetailsLoaded here: it would run on every details update (e.g. after
            // creating a comment). Scroll/expand only on initial load (see loading subscription).
          }
        }
      });

    this.store.select(smartInboxSelectors.selectGetLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        const wasLoading = this.conversationDetailsLoading;
        this.conversationDetailsLoading = loading.conversationDetails;

        if (!wasLoading && loading.conversationDetails && !this.activeConversation) {
          this.isDeepLinkLoading = true;
        }

        if (wasLoading && !loading.conversationDetails) {
          this.isDeepLinkLoading = false;
          if (this.activeConversationDetails) {
            this.store.select(smartInboxSelectors.selectActiveCommentId).pipe(take(1)).subscribe((activeCommentId) => {
              this.onDetailsLoaded(this.activeConversationDetails!, activeCommentId ?? undefined);
            });
          }
        }
      });

    this.store.select(smartInboxSelectors.selectSelectedConversationIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ids) => { this.selectedConversationIds = ids; });

    this.store.select(smartInboxSelectors.selectBulkMarkAsRead)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => { this.bulkMarkAsRead = value; });

    this.store.select(smartInboxSelectors.selectAccounts)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => { this.accounts = accounts; });

    this.store.select(smartInboxSelectors.selectMailboxTagAlwaysVisible)
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => { this.mailboxTagAlwaysVisible = visible; });

    this.store.select(smartInboxSelectors.selectMailboxTagColors)
      .pipe(takeUntil(this.destroy$))
      .subscribe((colors) => { this.mailboxTagColors = colors; });

    this.mailboxTagPreference.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe((display) => { this.mailboxTagDisplay = display; });

    this.store.select(smartInboxSelectors.selectFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe((filter) => { this.searchTerm = filter.searchTerm ?? ''; });

    this.store.select(smartInboxSelectors.selectActiveFolder)
      .pipe(takeUntil(this.destroy$))
      .subscribe((folder) => { this.activeFolder = folder; });

    this.store.select(smartInboxSelectors.selectActiveMessageId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((messageId) => { this.activeMessageId = messageId; });

    this.store.select(smartInboxSelectors.selectUpdateLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.classifyingMessageIds = loading.messageClassificationIds;
        this.updatingUrgency = loading.threadUrgency;
      });

    // The label reducer patches `labels` and never touches `objects`, so the
    // timeline keeps showing the state from before the change until the thread
    // is refetched. Assignment gets this for free -- its success handler
    // refetches the whole thread -- and doing the same per label toggle is what
    // this defers instead.
    this.actions$.pipe(
      ofType(smartInboxActions.UpdateThreadLabelsSuccess),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      // Detaching a chip in the header happens with the picker shut, and there
      // is no close event coming to carry a deferred refetch.
      if (this.labelMenuIsOpen) {
        this.labelTimelineStale = true;
        return;
      }
      this.reloadThreadTimeline();
    });

    this.actions$.pipe(
      ofType(smartInboxActions.UpdateConversationsSuccess, smartInboxActions.UpdateConversationsError),
      takeUntil(this.destroy$),
    ).subscribe((action) => {
      this.updatingCategory = false;
      this.updatingStatus = false;
      this.updatingAssignee = false;
      this.updatingCustomer = false;
      if (action.type === smartInboxActions.UpdateConversationsSuccess.type && this.activeConversation?.id != null) {
        const successAction = action as ReturnType<typeof smartInboxActions.UpdateConversationsSuccess>;
        if (successAction.conversationIds.includes(this.activeConversation.id)) {
          this.scrollTimelineToBottom()
        }
      }
    });

    // Bring the new card into view, and stop there: the artifact already shows
    // the reference, the customer, the line table and the total, so popping the
    // correction panel would take the user out of the conversation to fix
    // nothing in particular. Opening it stays an explicit gesture — the eye
    // action, a row, or "+N more".
    this.actions$.pipe(
      ofType(smartInboxActions.OrderCreatedReceived),
      filter(({ threadId }) => threadId === this.activeConversation?.id),
      switchMap(({ threadId }) => this.actions$.pipe(
        ofType(smartInboxActions.LoadConversationDetailsSuccess),
        filter(({ details }) => details?.id === threadId),
        take(1),
      )),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.scrollTimelineToBottom();
    });

    this.actions$.pipe(
      ofType(smartInboxActions.CreateCommentSuccess),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.commentInputValue = '';
      this.selectedMentionIds = [];
      this.resetCommentInputHeight('comment');
      this.scrollTimelineToBottom();
    });

    this.actions$.pipe(
      ofType(smartInboxActions.UpdateCommentSuccess, smartInboxActions.UpdateCommentError),
      takeUntil(this.destroy$),
    ).subscribe((action) => {
      this.loadingCommentId = null;
      if (action.type === smartInboxActions.UpdateCommentSuccess.type) {
        const success = action as ReturnType<typeof smartInboxActions.UpdateCommentSuccess>;
        if (success.commentId === this.editingCommentId) {
          this.resetCommentInputHeight('edit');
          this.editingCommentId = null;
          this.editedCommentBody = '';
          this.selectedEditMentionIds = [];
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Computed (template getters) ────────────────────────────────────────────

  /**
   * Memoised on `(details, view mode)`, the only two inputs it reads: the getter
   * is bound from the template of a default-change-detection component, so it
   * runs on every tick — every keystroke in the comment box, every tooltip, every
   * settled request. Recomputing was already wasteful, but since WB-7598 it also
   * minted a fresh `ITimelineOrderGroup` each time, whose new identity re-fired
   * the grouped card's `@Input` setter and rebuilt all its rows despite OnPush.
   */
  get timelineItems(): IConversationTimelineObject[] | null {
    const cache = this.timelineItemsCache;
    if (
      cache &&
      cache.details === this.activeConversationDetails &&
      cache.viewMode === this.timelineViewMode
    ) {
      return cache.items;
    }

    const items = this.computeTimelineItems();
    this.timelineItemsCache = {
      details: this.activeConversationDetails,
      viewMode: this.timelineViewMode,
      items,
    };
    return items;
  }

  private computeTimelineItems(): IConversationTimelineObject[] | null {
    // A deleted order is gone as far as the user is concerned — never render it,
    // whatever the view mode. The thread payload keeps returning it.
    const objects = this.activeConversationDetails?.objects?.filter(
      (obj) => !(isTimelineOrder(obj) && DELETED_ORDER_STATES.has(obj.order.state))
    );
    if (!objects?.length) return null;

    switch (this.timelineViewMode) {
      case TimelineViewMode.EMAILS:
        return objects.filter((obj) => isTimelineMessage(obj));
      case TimelineViewMode.COMMENTS:
        return objects.filter((obj) => isTimelineComment(obj));
      case TimelineViewMode.SIMPLE:
        // Compact: drop system events and anything produced by an automation.
        //
        // A label change IS a system event here, whoever caused it, so SIMPLE
        // hides `label_applied` / `label_removed` exactly as it already hides an
        // assignment or a status change (PROV-03 asks for the same timeline as
        // those two, not for different treatment inside it). The alternative —
        // keeping the ones a user caused — would make SIMPLE inconsistent for no
        // stated requirement: a user's own status change would stay hidden while
        // their label change showed. The chips in the sidebar are the compact
        // view of a thread's labels; this filter is about the narrative.
        return groupTimelineOrders(
          objects.filter((obj) => !isTimelineEvent(obj) && !this.isAutomationTimelineObject(obj))
        );
      case TimelineViewMode.FULL:
      default:
        // WB-7598 — collapse the orders of one create-order run into a single
        // artifact. Runs last, on the already-filtered list, so a deleted member
        // is gone before it can be counted: a 2-order run with one deleted falls
        // back to the single-order card rather than advertising "2 Orders created".
        return groupTimelineOrders(objects);
    }
  }

  /** i18n key for the label shown on the header "View" trigger button. */
  get activeTimelineViewLabelKey(): string {
    return (
      TIMELINE_VIEW_OPTIONS.find((opt) => opt.mode === this.timelineViewMode)?.labelKey ??
      TIMELINE_VIEW_OPTIONS[0].labelKey
    );
  }

  /** True when a timeline object was produced by an automation (message/comment/event). */
  private isAutomationTimelineObject(obj: IConversationTimelineObject): boolean {
    if (isTimelineMessage(obj)) return !!obj.sent_by_automation;
    if (isTimelineComment(obj) || isTimelineEvent(obj)) return obj.source === TimelineEventSource.AUTOMATION;
    return false;
  }

  getConversationTitle(): string {
    if (this.activeConversation?.customer) {
      return this.activeConversation.customer.company_name + ' – ' + this.activeConversation.customer.location;
    }
    const last = this.getLastMessageForDisplay();
    return last?.sender_name || 'Unknown';
  }

  getConversationSender(): string {
    const last = this.getLastMessageForDisplay();
    return last?.sender_email || '';
  }

  /** True when the active conversation is linked to a customer/company. */
  hasAssociatedCompany(): boolean {
    return !!(this.activeConversation?.customer || this.activeConversationDetails?.customer);
  }

  // ── Navbar buttons ─────────────────────────────────────────────────────────

  /** Change the timeline "View" filter and persist the choice for next time. */
  setTimelineViewMode(mode: TimelineViewMode): void {
    this.timelineViewMode = mode;
    try {
      localStorage.setItem(this.TIMELINE_VIEW_STORAGE_KEY, mode);
    } catch {
      // Ignore storage failures (private mode / quota) — the in-memory choice still applies.
    }
  }

  /** Restore the persisted timeline "View" filter (called on init). */
  private restoreTimelineViewMode(): void {
    try {
      const stored = localStorage.getItem(this.TIMELINE_VIEW_STORAGE_KEY);
      if (stored && TIMELINE_VIEW_OPTIONS.some((opt) => opt.mode === stored)) {
        this.timelineViewMode = stored as TimelineViewMode;
      }
    } catch {
      // Ignore storage failures — fall back to the default (FULL).
    }
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  // ── Message expansion ──────────────────────────────────────────────────────

  isMessageExpanded(messageId: number | string): boolean {
    return this.expandedMessageIds.has(messageId);
  }

  toggleMessage(messageId: number | string): void {
    if (this.expandedMessageIds.has(messageId)) {
      this.expandedMessageIds.delete(messageId);
    } else {
      this.expandedMessageIds.add(messageId);
    }
    this.expandedMessageIds = new Set(this.expandedMessageIds);
  }

  getMessagePreview(html: string, maxLength = 80): string {
    const cached = this.previewCache.get(html);
    if (cached !== undefined) return cached;

    const cleaned = getCleanedHtmlString(html);
    const doc = new DOMParser().parseFromString(cleaned, 'text/html');
    const raw = (doc.body.textContent ?? '').trim().replace(/\s+/g, ' ');
    const preview = raw.length <= maxLength ? raw : raw.slice(0, maxLength) + '…';
    this.previewCache.set(html, preview);
    return preview;
  }

  // ── Quote splitting & toggling ─────────────────────────────────────────────

  getMessageSplit(body: string): SplitEmailResult {
    const cached = this.splitCache.get(body);
    if (cached) return cached;

    const result = splitEmailQuote(body);
    this.splitCache.set(body, result);
    return result;
  }

  getReplyHtml(body: string): SafeHtml {
    const key = '__reply__' + body;
    const cached = this.sanitizedHtmlCache.get(key);
    if (cached) return cached;

    const { replyHtml } = this.getMessageSplit(body);
    const result = sanitizeHtmlUtil(replyHtml, this.sanitizer);
    this.sanitizedHtmlCache.set(key, result);
    return result;
  }

  getQuotedHtml(body: string): SafeHtml | null {
    const { quotedHtml } = this.getMessageSplit(body);
    if (!quotedHtml) return null;

    const key = '__quoted__' + body;
    const cached = this.sanitizedHtmlCache.get(key);
    if (cached) return cached;

    const result = sanitizeHtmlUtil(quotedHtml, this.sanitizer);
    this.sanitizedHtmlCache.set(key, result);
    return result;
  }

  hasQuotedContent(body: string): boolean {
    return this.getMessageSplit(body).quotedHtml !== null;
  }

  isQuoteExpanded(messageId: number | string): boolean {
    return this.expandedQuoteIds.has(messageId);
  }

  toggleQuote(messageId: number | string, event: Event): void {
    event.stopPropagation();
    if (this.expandedQuoteIds.has(messageId)) {
      this.expandedQuoteIds.delete(messageId);
    } else {
      this.expandedQuoteIds.add(messageId);
    }
    this.expandedQuoteIds = new Set(this.expandedQuoteIds);
  }

  // ── Conversation actions ──────────────────────────────────────────────────

  setActiveConversationMenu(menu: ConversationMenuOptions, updateType: 'single' | 'bulk' = 'single'): void {
    this.activeConversationMenu = menu;
    this.conversationUpdateType = updateType;
  }

  /**
   * Opens the shared company picker. The header's trigger is narrower than the
   * panel, so the width var is cleared back to the stylesheet default instead of
   * inheriting whatever the sidebar last measured.
   */
  onOpenCustomerMenu(): void {
    document.documentElement.style.removeProperty('--company-picker-width');
    this.customerMenuIsOpen = true;
  }

  closeCustomerMenu(): void {
    this.customerAssociationMenuTrigger?.closeMenu();
    this.customerAssociationMenuTriggerBulk?.closeMenu();
  }

  onCustomerSelected(customer: IConversationCustomer): void {
    this.onConversationUpdated({ customer_id: customer.id });
  }

  /** Skips update if the value hasn't changed (single-conversation mode). */
  onConversationUpdated(payload: IConversationUpdate): void {
    const isSingle = this.conversationUpdateType === 'single';
    const conversation = this.activeConversation ?? this.activeConversationDetails;

    if (isSingle) {
      if (!conversation) return;
      if (payload.category !== undefined && conversation.classification === payload.category) return;
      if (payload.status !== undefined && conversation.status === payload.status) return;
      if (payload.assignee_id !== undefined && conversation.assignee?.id === payload.assignee_id) return;
      if (payload.customer_id !== undefined && conversation.customer?.id === payload.customer_id) return;
    }

    if (!isSingle && !this.selectedConversationIds.length) return;

    const conversationIds = isSingle ? [conversation!.id] : [...this.selectedConversationIds];

    if (isSingle) {
      if (payload.category !== undefined) this.updatingCategory = true;
      if (payload.status !== undefined) this.updatingStatus = true;
      if (payload.assignee_id !== undefined) this.updatingAssignee = true;
      if (payload.customer_id !== undefined) this.updatingCustomer = true;
    }

    this.store.dispatch(smartInboxActions.UpdateConversations({ conversationIds, payload }));
  }

  onMarkRead(): void {
    const isSingle = this.conversationUpdateType === 'single';
    const conversation = this.activeConversation ?? this.activeConversationDetails;

    if (isSingle) {
      if (!conversation || !conversation.is_read) return;
    }
    if (!isSingle && !this.selectedConversationIds.length) return;

    const conversationIds = isSingle ? [conversation!.id] : [...this.selectedConversationIds];
    const isRead = isSingle ? false : this.bulkMarkAsRead;
    this.store.dispatch(smartInboxActions.MarkConversationsRead({ conversationIds, isRead }));
  }

  onSelectionCleared(): void {
    this.store.dispatch(smartInboxActions.ClearSelection());
  }

  // ── Compose actions ───────────────────────────────────────────────────────

  replyToMessage(event: any, replyType: 'reply' | 'reply-all', message: ITimelineMessage): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.activeConversationDetails?.mailbox?.email) return;

    const mailbox = this.activeConversationDetails.mailbox;
    let cc = '';

    if (replyType === 'reply-all') {
      const fromEmail = mailbox.email!.toLowerCase();
      const toEmail = message.sender_email.toLowerCase();
      const recipientsList = message.recipients ? message.recipients.split(',').map((e) => e.trim()) : [];
      const ccStr = typeof message.cc === 'string' ? message.cc : '';
      const ccList = ccStr ? ccStr.split(',').map((e) => e.trim()) : [];
      cc = [...recipientsList, ...ccList]
        .filter((email) => email && email.toLowerCase() !== fromEmail && email.toLowerCase() !== toEmail)
        .join(', ');
    }

    this.composeService.openCompose({
      type: replyType,
      from: mailbox,
      to: message.sender_email,
      subject: `Re: ${message.subject}`,
      cc,
      reply_to_conversation: this.activeConversationDetails,
      reply_to_message: message,
    });
  }

  forwardMessage(event: any, message: ITimelineMessage): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.activeConversationDetails?.mailbox) return;

    this.composeService.openCompose({
      type: 'forward',
      from: this.activeConversationDetails.mailbox,
      subject: `Forward: ${message.subject}`,
      body: buildForwardEditorBody(message),
      reply_to_conversation: this.activeConversationDetails,
      reply_to_message: message,
    });
  }

  /** True for messages not sent by the mailbox itself (i.e. inbound/customer messages). */
  isInboundMessage(message: ITimelineMessage): boolean {
    return message.sender_email !== this.activeConversationDetails?.mailbox?.email;
  }

  /** Whether this message carries an AI verdict; gates the classification row on outbound mail. */
  isClassifiedMessage(message: ITimelineMessage): boolean {
    return isClassified(message.classification) || !!storedCounterparty(message.counterparty);
  }

  // ─── AI classification (category / intent / urgency) ──────────────────────

  /**
   * Categories rolled up from the thread's messages, most recent first. These are
   * read-only here — corrections are made per message in the timeline.
   */
  get threadCategories(): ConversationClassification[] {
    const conversation = this.activeConversationDetails ?? this.activeConversation;
    if (!conversation) return [];
    if (conversation.categories?.length) return orderCategories(conversation.categories);
    return conversation.classification ? [conversation.classification] : [];
  }

  /** The conversation's counterparty to badge in the header, or `undefined`. */
  get badgedThreadCounterparty(): ConversationCounterparty | undefined {
    const conversation = this.activeConversationDetails ?? this.activeConversation;
    return badgedCounterparty(conversation?.counterparty);
  }

  /** Categories shown inline in the header before the rest collapse into "+n". */
  get visibleThreadCategories(): ConversationClassification[] {
    return this.threadCategories.slice(0, MAX_VISIBLE_THREAD_CATEGORIES);
  }

  /** Categories behind the "+n" chip, revealed on hover. */
  get hiddenThreadCategories(): ConversationClassification[] {
    return this.threadCategories.slice(MAX_VISIBLE_THREAD_CATEGORIES);
  }

  /** Recipient-mailbox tag for the open thread, or `null` when it stays hidden. */
  get mailboxTag(): IMailboxTag | null {
    const mailbox = this.activeConversationDetails?.mailbox ?? this.activeConversation?.mailbox;
    return buildMailboxTag(mailbox, {
      enabled: this.mailboxTagPreference.showOnHeader(),
      alwaysVisible: this.mailboxTagAlwaysVisible,
      colors: this.mailboxTagColors,
    });
  }

  /** Effective thread urgency — the operator override wins over the AI. */
  get isThreadUrgent(): boolean {
    return !!(this.activeConversationDetails?.is_urgent ?? this.activeConversation?.is_urgent);
  }

  isClassifying(messageId: number | string): boolean {
    return this.classifyingMessageIds.includes(Number(messageId));
  }

  /**
   * Whether the intent picker is shown for a message. An uncategorised message
   * has nothing meaningful to pick from, so category comes first.
   */
  hasIntentPicker(message: ITimelineMessage): boolean {
    return !!message.classification && message.classification !== ConversationClassification.UNDEFINED;
  }

  /**
   * Intents offered for a message, scoped to its current category. An intent the
   * classifier assigned that is outside that list is prepended, so the current
   * value is always visible and check-marked rather than silently missing.
   */
  getIntentOptions(message: ITimelineMessage): string[] {
    const category = (message.classification || ConversationClassification.UNDEFINED) as ConversationClassification;
    const options = MESSAGE_INTENTS_BY_CATEGORY[category] ?? [];
    const current = message.intent;
    return current && !options.includes(current) ? [current, ...options] : options;
  }

  /** Translated intent name; unknown intents from the classifier are humanized. */
  getIntentLabel(intent?: string | null): string {
    if (!intent) return this.translate.instant('smart_inbox.inbox_view.detail.intent_none');
    const key = intentLabelKey(intent);
    const translated = this.translate.instant(key);
    return translated === key ? humanizeIntent(intent) : translated;
  }

  onMessageCategorySelected(message: ITimelineMessage, category: ConversationClassification): void {
    if (message.classification === category) return;
    this.dispatchMessageClassification(message, { category });
  }

  onMessageIntentSelected(message: ITimelineMessage, intent: string | null): void {
    if ((message.intent ?? null) === intent) return;
    this.dispatchMessageClassification(message, { intent });
  }

  /** A message with no stored counterparty reads as `unknown` in the picker. */
  messageCounterparty(message: ITimelineMessage): ConversationCounterparty {
    return toCounterparty(message.counterparty);
  }

  onMessageCounterpartySelected(message: ITimelineMessage, counterparty: ConversationCounterparty): void {
    if (this.messageCounterparty(message) === counterparty) return;
    if (confirmNeedsCompany(counterparty)) {
      this.pendingCustomerConfirmation = message;
      return;
    }
    this.dispatchMessageClassification(message, { counterparty });
  }

  /** Whether this menu pick opens the company picker instead of sending. */
  counterpartyPickNeedsCompany(message: ITimelineMessage, counterparty: ConversationCounterparty): boolean {
    return confirmNeedsCompany(counterparty) && this.messageCounterparty(message) !== counterparty;
  }

  /** Whether the "to verify" chip is a confirm button; never for `unknown`, which would clear the memory. */
  canConfirmCounterparty(message: ITimelineMessage): boolean {
    // Imported helper, not `this.isToVerify`: specs build the component off the prototype.
    return isToVerify(message.counterparty_status)
        && !this.isDualRoleMessage(message)
        && this.messageCounterparty(message) !== ConversationCounterparty.UNKNOWN;
  }

  /** Whether this message comes from a contact declared to play both sides. */
  isDualRoleMessage(message: ITimelineMessage): boolean {
    return message.counterparty_dual_role === true;
  }

  /**
   * Confirm the displayed verdict; `customer` first asks for a company.
   * Not via `onMessageCounterpartySelected`, which drops an unchanged value.
   */
  confirmMessageCounterparty(message: ITimelineMessage): void {
    if (!this.canConfirmCounterparty(message)) return;
    if (confirmNeedsCompany(this.messageCounterparty(message))) {
      this.pendingCustomerConfirmation = message;
      return;
    }
    this.dispatchMessageClassification(message, {
      counterparty: this.messageCounterparty(message),
    });
  }

  /** The company picked for a pending `customer` confirmation, sent in one payload. */
  onConfirmCustomerCompany(customer: IConversationCustomer): void {
    const message = this.pendingCustomerConfirmation;
    this.pendingCustomerConfirmation = null;
    // The picker stops click propagation: close the menu chain as a click inside it would.
    this.confirmCustomerMenu?.closed.emit('click');
    if (!message) return;
    this.dispatchMessageClassification(message, {
      counterparty: ConversationCounterparty.CUSTOMER,
      customer_id: customer.id,
    });
  }

  /** Dismissing the picker confirms nothing. */
  cancelCustomerConfirmation(): void {
    this.pendingCustomerConfirmation = null;
  }

  /** Whether to offer the double role on this message. */
  showsCounterpartyConflict(message: ITimelineMessage): boolean {
    // The backend already clears the conflict; this also covers `useMockData`.
    return hasCounterpartyConflict(message.counterparty_conflict)
        && !this.isDualRoleMessage(message);
  }

  /** What the AI read on this message, as a tooltip line, or `''`. */
  modelReadingHint(message: ITimelineMessage): string {
    const reading = counterpartyModelReading(
      message.counterparty_model, message.counterparty_model_confidence);
    if (!reading) return '';
    return this.translate.instant('smart_inbox.inbox_view.detail.counterparty_model_reading', {
      model: this.translate.instant(counterpartyLabelKey(reading.value)),
      percent: reading.percent,
    });
  }

  /** Counterparty chip tooltip: source, evidence and the AI's read of this message. */
  counterpartyTooltip(message: ITimelineMessage, leadKey?: string): string {
    const head = [
      ...(leadKey ? [this.translate.instant(leadKey)] : []),
      this.translate.instant(counterpartySourceLabelKey(message.counterparty_source)),
      ...(message.counterparty_evidence ? [message.counterparty_evidence] : []),
    ].join(' — ');
    return this.withModelReading(head, message);
  }

  /** Dual-role chip tooltip: the operator is the source, so only the AI's read is added. */
  counterpartyDualRoleTooltip(message: ITimelineMessage): string {
    return this.withModelReading(
      this.translate.instant('smart_inbox.inbox_view.detail.counterparty_dual_role_hint'),
      message,
    );
  }

  private withModelReading(head: string, message: ITimelineMessage): string {
    const reading = this.modelReadingHint(message);
    return reading ? `${head}\n${reading}` : head;
  }

  /** Declare the double role; past verdicts are kept. */
  acceptDualRole(message: ITimelineMessage): void {
    this.dispatchMessageClassification(message, { dual_role: true });
  }

  private dispatchMessageClassification(message: ITimelineMessage, payload: IMessageClassificationUpdate): void {
    const conversationId = this.activeConversationDetails?.id;
    if (!conversationId) return;

    this.store.dispatch(smartInboxActions.UpdateMessageClassification({
      conversationId,
      messageId: Number(message.id),
      payload,
    }));
  }

  onToggleUrgency(): void {
    const conversationId = this.activeConversationDetails?.id ?? this.activeConversation?.id;
    if (!conversationId || this.updatingUrgency) return;

    this.store.dispatch(smartInboxActions.UpdateThreadUrgency({
      conversationId,
      isUrgent: !this.isThreadUrgent,
    }));
  }


  /**
   * One-click AI reply: opens the reply composer and lets it auto-trigger AI draft
   * generation as soon as its editor is ready (via the `autoAiDraft` flag), so the
   * draft starts generating the moment the composer opens. The composer shows its
   * own generating banner / failure handling — identical to the in-panel flow.
   */
  quickAiReply(event: Event, message: ITimelineMessage): void {
    event.preventDefault();
    event.stopPropagation();

    const details = this.activeConversationDetails;
    if (!details?.mailbox?.email || !details.id) return;

    this.composeService.openCompose({
      type: 'reply',
      from: details.mailbox,
      to: message.sender_email,
      subject: `Re: ${message.subject}`,
      reply_to_conversation: details,
      reply_to_message: message,
      autoAiDraft: true,
    });
  }

  /** Latest email message in the thread — the reply target for thread-level actions. */
  private getLatestMessage(): ITimelineMessage | null {
    const objects = this.activeConversationDetails?.objects;
    if (!objects?.length) return null;
    for (let i = objects.length - 1; i >= 0; i--) {
      const obj = objects[i];
      if (isTimelineMessage(obj)) return obj;
    }
    return null;
  }

  /** Thread-level AI draft: open the reply composer on the latest message and auto-generate. */
  aiDraftForThread(event: Event): void {
    const target = this.getLatestMessage();
    if (target) this.quickAiReply(event, target);
  }

  /** Toggle the inline internal-message (comment) composer in the action bar. */
  toggleInternalComposer(): void {
    this.internalComposerOpen = !this.internalComposerOpen;
    if (this.internalComposerOpen) {
      setTimeout(() => this.commentInputRef?.nativeElement?.focus());
    }
  }

  /** Close the internal-message composer and discard the current draft. */
  closeInternalComposer(): void {
    this.internalComposerOpen = false;
    this.commentInputValue = '';
    this.selectedMentionIds = [];
  }

  downloadAttachment(attachment: ITimelineAttachment): void {
    this.conversationService.downloadAttachment(attachment.download_url, attachment.name, skipErrorToast()).subscribe({
      next: ({ blob, filename }) => {
        this.triggerBlobDownload(blob, filename);
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.attachment_downloaded'), 'INFO', {
          duration: 5000, verticalPosition: 'bottom', horizontalPosition: 'right',
        });
      },
      error: () => {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.failed_download_attachment'), 'ERROR', this.toastOptions);
      },
    });
  }

  /** True when the attachment type can be rendered inline by <app-files-previewer>. */
  isPreviewable(attachment: ITimelineAttachment): boolean {
    const name = (attachment?.name ?? '').toLowerCase().split('?')[0];
    const ext = name.includes('.') ? (name.split('.').pop() ?? '') : '';
    return PREVIEWABLE_ATTACHMENT_EXTENSIONS.has(ext);
  }

  /** Open an inline preview of the attachment (reuses the cached download endpoint). */
  previewAttachment(attachment: ITimelineAttachment): void {
    // A fetcher, not the bytes: the dialog is shared with the pricing screens,
    // which read an ir.attachment instead of streaming through Unipile. What
    // they have in common is the three fields it hands back.
    this.filePreview.open({
      filename: attachment.name,
      source: () => this.conversationService.downloadAttachment(
        attachment.download_url, attachment.name,
      ),
    });
  }

  // ── Timeline order actions ──────────────────────────────────────────────────

  openOrderCorrectionPanel(timelineOrder: ITimelineOrder): void {
    this.openSideBySideCorrectionPanel(timelineOrder);
  }

  downloadQuotationPdf(order: ITimelineOrder): void {
    this.quotesService.getPdfQuotation(order.order.id, skipErrorToast()).subscribe({
      next: (blob) => {
        this.triggerBlobDownload(blob, `${order.order.name}.pdf`);
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.quotation_pdf_downloaded'), 'INFO', this.toastOptions);
      },
      error: () => {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.failed_download_quotation_pdf'), 'ERROR', this.toastOptions);
      },
    });
  }

  /** Open the standalone order page — the artifact's "View full order" action. */
  openOrderInNewTab(timelineOrder: ITimelineOrder): void {
    window.open(`${window.location.origin}/quotations/${timelineOrder.order.id}`, '_blank');
  }

  /** Refetch the thread after the artifact changed the order's status. */
  reloadActiveConversationDetails(): void {
    const conversationId = this.activeConversation?.id;
    if (conversationId == null) return;
    this.store.dispatch(smartInboxActions.LoadConversationDetails({ conversationId }));
  }

  openSideBySideCorrectionPanel(order: ITimelineOrder): void {
    // window.open(`${window.location.origin}/quotations/${order.order.id}`, '_blank');
    // return;
    const quotationId = order.order.id;
    this.dialog.open(SideBySideCorrectionPanelComponent, {
      data: { quotationId, origin: 'smart-inbox' },
      panelClass: ['side-panel-slide-in', 'side-panel-full'],
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      // The timeline renders `order.state` from the smart-inbox store, which knows
      // nothing about the quotations store the panel mutates. Without this the card
      // keeps showing pre-mutation state (status, or a cancelled/deleted order still
      // listed) until a page reload.
      if (!sideBySideResultMutatedOrder(result)) return;
      const conversationId = this.activeConversationDetails?.id;
      if (conversationId != null) {
        this.store.dispatch(smartInboxActions.LoadConversationDetails({ conversationId }));
      }
    });
  }

  // ── Comment (create) ───────────────────────────────────────────────────────

  getMentionables(): { id: number; name: string; type: string }[] {
    return this.users.map((user) => ({
      id: user.id,
      name: getUserFullname(user),
      initials: getUserInitials(user),
      type: 'user',
    }));
  }

  onMentionSearch(_query: string): { id: number; name: string; type: string }[] {
    return [];
  }

  onMentionSelected(item: { id: number; name: string; type: string }, isEdit: boolean): void {
    if (isEdit) {
      this.selectedEditMentionIds.push(item.id);
    } else {
      this.selectedMentionIds.push(item.id);
      this.mentionJustSelected = true;
    }
  }

  onCommentKeydown(event: KeyboardEvent, type: 'comment' | 'edit'): void {
    if (event.key !== 'Enter' || event.shiftKey || this.mentionListOpen) return;
    if (this.mentionJustSelected) {
      this.mentionJustSelected = false;
      return;
    }
    event.preventDefault();
    if (type === 'comment') this.createComment();
    else this.confirmEditComment();
  }

  growCommentInput(type: 'comment' | 'edit'): void {
    const el = type === 'comment' ? this.commentInputRef?.nativeElement : this.commentEditInputRef?.nativeElement;
    const value = type === 'comment' ? this.commentInputValue : this.editedCommentBody;
    if (!el) return;
    const min = type === 'comment' ? 64 : 40;
    const max = 128;
    if (!value.includes('\n')) {
      el.style.height = `${min}px`;
      el.style.overflowY = 'hidden';
      return;
    }
    el.style.height = 'auto';
    const height = Math.min(Math.max(el.scrollHeight, min), max);
    el.style.height = `${height}px`;
    el.style.overflowY = height >= max ? 'auto' : 'hidden';
  }

  /** Restore comment or edit input to its original (min) height after send or save. */
  resetCommentInputHeight(type: 'comment' | 'edit'): void {
    requestAnimationFrame(() => {
      const el = type === 'comment' ? this.commentInputRef?.nativeElement : this.commentEditInputRef?.nativeElement;
      const min = type === 'comment' ? 64 : 40;
      if (el) {
        el.style.height = `${min}px`;
        el.style.overflowY = 'hidden';
      }
    });
  }

  createComment(): void {
    if (this.mentionListOpen) return;
    if (this.commentInputValue.trim().length === 0) return;
    const payload = this.buildMessageWithMentionIds(this.commentInputValue, this.selectedMentionIds);
    const conversationId = (this.activeConversation ?? this.activeConversationDetails)?.id;
    if (!conversationId) return;
    this.store.dispatch(smartInboxActions.CreateComment({ conversationId, message: payload }));
  }

  // ── Comment (display) ─────────────────────────────────────────────────────

  formatMentionIdsToDisplayNames(message: string): string {
    const mentionables = this.getMentionables();
    const idToName = new Map(mentionables.map((m) => [m.id, m.name]));
    return message.replace(/@(\d+)(?=[\s\W]|$)/g, (_, idStr) => {
      const id = Number(idStr);
      const name = idToName.get(id);
      return name != null ? `@<${name}>` : `@<${idStr}>`;
    });
  }

  getMentionsFromBody(body: string): { id: number; name: string; email: string }[] {
    const ids = new Set<number>();
    const match = body.matchAll(/@(\d+)(?=[\s\W]|$)/g);
    for (const m of match) ids.add(Number(m[1]));
    return [...ids].map((id) => {
      const user = this.users.find((u) => u.id === id);
      return user
        ? { id: user.id, name: getUserFullname(user), email: user.email ?? '' }
        : { id, name: String(id), email: '' };
    });
  }

  getCommentBodyHtml(body: string, mentions: { id: number; name: string; email: string }[] = []): SafeHtml {
    const text = this.formatMentionIdsToDisplayNames(body);
    let escaped = this.escapeHtmlForMentions(text);
    escaped = escaped.replace(/\n/g, '<br>');
    const resolved = mentions?.length ? mentions : this.getMentionsFromBody(body);
    if (!resolved.length) return this.sanitizer.bypassSecurityTrustHtml(escaped);
    const byName = [...resolved].sort((a, b) => b.name.length - a.name.length);
    let html = escaped;
    for (const m of byName) {
      const escapedName = this.escapeHtmlForMentions(m.name);
      const pattern = `@&lt;${escapedName}&gt;`;
      const span = `<span class="mention" data-user-id="${m.id}" data-user-name="${escapedName.replace(/"/g, '&quot;')}" role="button" tabindex="0">@${escapedName}</span>`;
      html = html.split(pattern).join(span);
    }
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  onMentionClick(event: Event): void {
    const target = (event.target as HTMLElement).closest?.('.mention');
    if (!target) return;
    const userId = (target as HTMLElement).getAttribute('data-user-id');
    const userName = (target as HTMLElement).getAttribute('data-user-name');
    if (userId) {
      // Handle mention click (e.g. open profile, show tooltip)
    }
  }

  // ── Comment (edit & actions) ────────────────────────────────────────────────

  canEditComment(item: IConversationTimelineObject): boolean {
    return isTimelineComment(item) && !!this.currentUser && item.author.id === this.currentUser.id;
  }

  startEditingComment(item: IConversationTimelineObject): void {
    if (!isTimelineComment(item) || !this.canEditComment(item)) return;
    const body = this.getBodyForMentionInput(item.body);
    const ids = this.getMentionIdsFromBodyInOrder(item.body);
    // When switching to another comment, clear edit state first so the input shows the new comment's original value
    if (this.editingCommentId !== null && this.editingCommentId !== item.id) {
      this.editingCommentId = null;
      this.editedCommentBody = '';
      this.selectedEditMentionIds = [];
      setTimeout(() => {
        this.editingCommentId = item.id;
        this.editedCommentBody = body;
        this.selectedEditMentionIds = ids;
        this.growCommentInput('edit');
      }, 0);
    } else {
      this.editingCommentId = item.id;
      this.editedCommentBody = body;
      this.selectedEditMentionIds = ids;
      setTimeout(() => this.growCommentInput('edit'), 0);
    }
  }

  cancelEditComment(): void {
    this.editingCommentId = null;
    this.editedCommentBody = '';
    this.selectedEditMentionIds = [];
  }

  confirmEditComment(): void {
    if (this.editingCommentId == null || !this.activeConversation?.id || !this.editedCommentBody.trim()) return;
    this.loadingCommentId = this.editingCommentId;
    const body = this.buildMessageWithMentionIds(this.editedCommentBody, this.selectedEditMentionIds).trim();
    this.store.dispatch(smartInboxActions.UpdateComment({
      conversationId: this.activeConversation.id,
      commentId: this.editingCommentId,
      body,
    }));
  }

  onCommentDelete(item: IConversationTimelineObject): void {
    if (!isTimelineComment(item) || !this.canEditComment(item) || !this.activeConversation?.id) return;
    const dialogRef = this.dialog.open(InboxConfirmationDialogComponent, {
      data: { actionType: InboxConfirmationActionType.DELETE_COMMENT },
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (confirmed) {
        const conversationId = (this.activeConversation ?? this.activeConversationDetails)?.id;
        if (!conversationId) return;
        this.store.dispatch(smartInboxActions.DeleteComment({
          conversationId,
          commentId: item.id,
        }));
      }
    });
  }

  copyComment(item: IConversationTimelineObject): void {
    if (!isTimelineComment(item)) return;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(item.body).then(
        () => this.commonService.showToast(this.translate.instant('smart_inbox.toasts.copied_to_clipboard'), 'INFO', this.toastOptions),
        () => this.fallbackCopyToClipboard(item.body),
      );
    } else {
      this.fallbackCopyToClipboard(item.body);
    }
  }

  // ── Create order ───────────────────────────────────────────────────────

  /**
   * True while *this* conversation's create-order job runs — read off the
   * skeleton the reducer keeps attached to the thread, so it survives the user
   * browsing away and back during the ~2 min the job takes (WB-7355).
   */
  get creatingOrder(): boolean {
    return (this.activeConversationDetails?.objects ?? []).some(isTimelineOrderPlaceholder);
  }

  /** Dispatches CreateOrder for the active conversation; loading state cleared by the completion event. */
  createOrder(): void {
    if (!this.activeConversation?.id || this.creatingOrder) return;
    this.store.dispatch(smartInboxActions.CreateOrder({ conversationId: this.activeConversation.id }));
    setTimeout(() => this.scrollTimelineToBottom(), 200);
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  sanitizeHtml(html: string): SafeHtml {
    const cached = this.sanitizedHtmlCache.get(html);
    if (cached !== undefined) return cached;

    const result = sanitizeHtmlUtil(html, this.sanitizer);
    this.sanitizedHtmlCache.set(html, result);
    return result;
  }

  trackByTimelineItem(_index: number, item: IConversationTimelineObject): number | string {
    return item.id;
  }

  getHumanReadableTimelineEvent(event: ITimelineEvent): { icon: string; message: string } {
    return { icon: getIconForTimelineEvent(event), message: getHumanReadableTimelineEvent(event, this.translate) };
  }

  getTimelineActorName(item: ITimelineEvent | ITimelineComment): string {
    const own = isTimelineComment(item) ? item.author.name : item.user?.name ?? '';
    return getTimelineActorLabel(item.source, own, this.translate);
  }

  getAutomationTooltip(item: ITimelineEvent | ITimelineComment): string {
    if (item.source !== TimelineEventSource.AUTOMATION) return '';
    return item.automation_name ?? '';
  }

  /** Opens the draft a send_reply automation created, from its timeline event.
   *  The backend only exposes draft_id while the draft is still openable, so a
   *  race (draft sent/deleted since render) degrades to a toast. */
  openDraftFromEvent(item: ITimelineEvent): void {
    const draftId = item.draft_id;
    if (!draftId) return;
    this.draftService.getDraft(draftId).pipe(take(1)).subscribe({
      next: (res) => {
        if (res?.object) {
          this.composeService.openFromDraft(res.object);
        } else {
          this.commonService.showToast(
            this.translate.instant('smart_inbox.toasts.draft_unavailable'), 'WARNING', this.toastOptions,
          );
        }
      },
      error: () => this.commonService.showToast(
        this.translate.instant('smart_inbox.toasts.draft_unavailable'), 'WARNING', this.toastOptions,
      ),
    });
  }

  getDisplaySenderName(item: ITimelineMessage): string {
    const { name: mailboxName, email: mailboxEmail } = this.activeConversationDetails?.mailbox ?? {};
    if (!mailboxEmail || !item.sender_email) return item.sender_name;
    return item.sender_email.toLowerCase() === mailboxEmail.toLowerCase() ? mailboxName ?? this.translate.instant('smart_inbox.inbox_view.detail.recipient_me') : item.sender_name;
  }

  getDisplayRecipients(recipients: string): string {
    const mailboxEmail = this.activeConversationDetails?.mailbox?.email;
    if (!mailboxEmail || !recipients) return recipients;
    const meLabel = this.translate.instant('smart_inbox.inbox_view.detail.recipient_me');
    const names = recipients
      .split(',')
      .map((r) => r.trim().toLowerCase() === mailboxEmail.toLowerCase() ? meLabel : r.trim())
      .filter((r) => r.length > 0);
    if (names.length <= MAX_DISPLAYED_RECIPIENTS) return names.join(', ');
    const moreLabel = this.translate.instant('smart_inbox.inbox_view.detail.recipients_more', {
      count: names.length - MAX_DISPLAYED_RECIPIENTS,
    });
    return `${names.slice(0, MAX_DISPLAYED_RECIPIENTS).join(', ')}, ${moreLabel}`;
  }

  hasReplyAllRecipients(msg: ITimelineMessage): boolean {
    const cc = typeof msg.cc === 'string' ? msg.cc : '';
    const bcc = typeof (msg as { bcc?: string }).bcc === 'string' ? (msg as { bcc?: string }).bcc : '';
    return (cc?.length ?? 0) > 0 || (bcc?.length ?? 0) > 0;
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private getLastMessageForDisplay(): ITimelineMessage | null {
    const details = this.activeConversationDetails;
    if (!details) return null;
    if (details.objects?.length) {
      for (let i = details.objects.length - 1; i >= 0; i--) {
        const obj = details.objects[i];
        if (isTimelineMessage(obj) && !obj.in_reply_to) return obj;
      }
      return null;
    }
    return null;
  }

  /** When details load: expand the target message; scroll to it (highlighting a
   * specific sent message or comment when one was requested), else the last message. */
  private onDetailsLoaded(details: IConversationDetails, scrollToCommentId?: number): void {
    if (!details.objects?.length) return;

    const mailboxEmail = details.mailbox?.email?.toLowerCase();
    const isSentFolder = this.activeFolder === InboxFolderType.SENT;
    // A specific sent message was clicked: highlight exactly that message.
    const highlightMessageId = this.activeMessageId;

    let targetMessageId: number | string | null = null;

    if (highlightMessageId != null) {
      const found = details.objects.find((obj) => isTimelineMessage(obj) && obj.id === highlightMessageId);
      if (found) targetMessageId = found.id;
    }

    if (targetMessageId == null && isSentFolder && mailboxEmail) {
      for (let i = details.objects.length - 1; i >= 0; i--) {
        const obj = details.objects[i];
        if (isTimelineMessage(obj) && obj.sender_email?.toLowerCase() === mailboxEmail) {
          targetMessageId = obj.id;
          break;
        }
      }
    }

    if (targetMessageId == null) {
      for (let i = details.objects.length - 1; i >= 0; i--) {
        if (isTimelineMessage(details.objects[i])) {
          targetMessageId = details.objects[i].id;
          break;
        }
      }
    }

    if (targetMessageId != null) {
      this.expandedMessageIds = new Set<number | string>([targetMessageId]);
    } else {
      this.expandedMessageIds = new Set();
    }

    setTimeout(() => {
      if (scrollToCommentId != null) {
        this.scrollToCommentAndHighlight(scrollToCommentId);
      } else if (highlightMessageId != null && targetMessageId != null) {
        this.scrollToMessageAndHighlight(targetMessageId);
      } else if (targetMessageId != null) {
        this.scrollToMessageById(details, targetMessageId);
      }
    }, 0);
  }

  private scrollToLastMessage(): void {
    const last = this.messageItemRefs?.last;
    if (last?.nativeElement) {
      last.nativeElement.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }

  /** Scroll to the timeline message with the given id. Ref index is the message index (messages only). */
  private scrollToMessageById(details: IConversationDetails, messageId: number | string | null): void {
    if (!messageId) this.scrollToLastMessage();
    const objects = details.objects ?? [];
    let messageIndex = 0;
    for (const obj of objects) {
      if (isTimelineMessage(obj)) {
        if (obj.id === messageId) {
          const el = this.messageItemRefs?.get(messageIndex)?.nativeElement;
          if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
          return;
        }
        messageIndex++;
      }
    }
    // Fallback to last message if refs not yet available or id not found
    this.scrollToLastMessage();
  }

  /** Scroll the timeline to the bottom after the new content is in the store and rendered. */
  private scrollTimelineToBottom(): void {
    const el = this.timelineContainerRef?.nativeElement;
    if (!el) return;
    const scrollToBottom = () => {
      el.scrollTop = el.scrollHeight - el.clientHeight;
    };
    // Delay so Angular has time to apply the store update and render the new element
    setTimeout(() => {
      requestAnimationFrame(scrollToBottom);
    }, 500);
  }

  /** Scroll to the timeline message with the given id and highlight it briefly (e.g. opened from the Sent list). */
  private scrollToMessageAndHighlight(messageId: number | string): void {
    const container = this.timelineContainerRef?.nativeElement;
    if (!container) return;

    const messageEl = container.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
    if (!messageEl) {
      this.store.dispatch(smartInboxActions.ClearActiveMessageId());
      return;
    }

    messageEl.scrollIntoView({ behavior: 'auto', block: 'start' });
    messageEl.classList.add('conversation-detail__timeline-message--highlight');
    setTimeout(() => {
      messageEl.classList.remove('conversation-detail__timeline-message--highlight');
      this.store.dispatch(smartInboxActions.ClearActiveMessageId());
    }, 1500);
  }

  /** Scroll timeline to the comment with the given id and highlight it with a primary ring for 1s (e.g. from mention list). */
  private scrollToCommentAndHighlight(commentId: number): void {
    const container = this.timelineContainerRef?.nativeElement;
    if (!container) return;

    const commentEl = container.querySelector<HTMLElement>(`[data-comment-id="${commentId}"]`);
    if (!commentEl) return;

    const gapBelow = 56;
    const containerRect = container.getBoundingClientRect();
    const commentRect = commentEl.getBoundingClientRect();
    const commentTopInContent = container.scrollTop + (commentRect.top - containerRect.top);
    const maxScroll = container.scrollHeight - container.clientHeight;
    const targetScroll = commentTopInContent + commentEl.offsetHeight + gapBelow - container.clientHeight;
    const clampedScroll = Math.max(0, Math.min(maxScroll, targetScroll));
    container.scrollTo({ top: clampedScroll, behavior: 'auto' });
    commentEl.classList.add('conversation-detail__timeline-comment--highlight');
    setTimeout(() => {
      commentEl.classList.remove('conversation-detail__timeline-comment--highlight');
      commentEl.blur();
      if (commentEl.contains(document.activeElement)) {
        (document.activeElement as HTMLElement)?.blur();
      }
      this.store.dispatch(smartInboxActions.ClearActiveCommentId());
    }, 1000);
  }

  /**
   * Converts stored body to the same format as the send input: @Name (no angle brackets).
   * Used when opening the edit input so it aligns with send. Handles both @id and @<Name> from API.
   */
  private getBodyForMentionInput(body: string): string {
    const mentionables = this.getMentionables();
    const idToName = new Map(mentionables.map((m) => [m.id, m.name]));
    // Strip @<Name> → @Name so edit matches send format
    let out = body.replace(/@<([^>]+)>/g, '@$1');
    // Replace @id with @Name
    out = out.replace(/@(\d+)(?=[\s\W]|$)/g, (_, idStr) => {
      const id = Number(idStr);
      const name = idToName.get(id);
      return name != null ? `@${name}` : `@${idStr}`;
    });
    return out;
  }

  /** Returns mention ids found in stored body in order (@42 @3 → [42, 3]). Shared for edit init. */
  private getMentionIdsFromBodyInOrder(body: string): number[] {
    const ids: number[] = [];
    const re = /@(\d+)(?=[\s\W]|$)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(body)) !== null) ids.push(Number(m[1]));
    return ids;
  }

  /**
   * Shared: builds API payload with @id instead of @Name. Used by both send and edit.
   * @param text current input text (commentInputValue or editedCommentBody)
   * @param selectedIds mention ids in order (selectedMentionIds or selectedEditMentionIds)
   */
  private buildMessageWithMentionIds(text: string, selectedIds: number[]): string {
    const mentionables = this.getMentionables();
    const byName = [...mentionables].sort((a, b) => b.name.length - a.name.length);
    const mentions: { start: number; end: number; id: number }[] = [];
    let i = 0;
    while (i < text.length) {
      if (text[i] === '@') {
        let matched = false;
        for (const m of byName) {
          const rest = text.slice(i + 1, i + 1 + m.name.length);
          if (rest === m.name) {
            mentions.push({ start: i, end: i + 1 + m.name.length, id: m.id });
            i = i + 1 + m.name.length;
            matched = true;
            break;
          }
        }
        if (!matched) i++;
      } else {
        i++;
      }
    }
    const ids = mentions.map((m, idx) => selectedIds[idx] ?? m.id);
    let payload = '';
    let last = 0;
    mentions.forEach((m, idx) => {
      payload += text.slice(last, m.start) + '@' + ids[idx];
      last = m.end;
    });
    payload += text.slice(last);
    return payload;
  }

  private escapeHtmlForMentions(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Triggers a browser download for a blob (used for attachments and quotation PDF). */
  private triggerBlobDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /** Fallback method to copy text to clipboard if clipboard API is not available. */
  private fallbackCopyToClipboard(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      const ok = document.execCommand('copy');
      if (ok) {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.copied_to_clipboard'), 'INFO', this.toastOptions);
      } else {
        this.commonService.showToast(this.translate.instant('smart_inbox.toasts.copy_failed'), 'WARNING', this.toastOptions);
      }
    } catch {
      this.commonService.showToast(this.translate.instant('smart_inbox.toasts.copy_failed'), 'WARNING', this.toastOptions);
    } finally {
      document.body.removeChild(textarea);
    }
  }

  // ── Labels (ASSIGN-01..04) ─────────────────────────────────────────────────
  //
  // In the header rather than the sidebar: a label sits at the same level as the
  // classification chips beside it, which is where the PO's design puts it.

  labelMenuIsOpen = false;

  /**
   * A label changed while the picker was open. The timeline is refetched once,
   * when the picker closes, rather than per toggle: the picker deliberately
   * stays open so several labels can be applied in one visit, and refetching
   * per click would be one full thread GET per checkbox.
   */
  private labelTimelineStale = false;

  /**
   * Details first, then the list row -- the same fallback `threadCategories`
   * uses, and for the same reason. Both response shapes carry `labels`, so the
   * chips can render from the row already in the store the moment a thread is
   * clicked instead of waiting on the details request. `??`, not `||`: once
   * details have loaded they are authoritative, and an empty array from them
   * means "no labels", not "fall back to a stale row".
   */
  get conversationLabels(): IConversationLabel[] {
    return this.activeConversationDetails?.labels ?? this.activeConversation?.labels ?? [];
  }

  /** Chips that stay on the row; the rest go behind "+n", as the categories do. */
  get visibleConversationLabels(): IConversationLabel[] {
    return this.conversationLabels.slice(0, MAX_VISIBLE_LABELS);
  }

  get hiddenConversationLabels(): IConversationLabel[] {
    return this.conversationLabels.slice(MAX_VISIBLE_LABELS);
  }

  trackByLabelId(_index: number, label: IConversationLabel): number {
    return label.id;
  }

  onApplyLabel(labelId: number): void {
    this.dispatchLabelUpdate({ add_label_ids: [labelId] });
  }

  onRemoveLabel(labelId: number): void {
    this.dispatchLabelUpdate({ remove_label_ids: [labelId] });
  }

  /** A name, not an id: the backend resolves or creates it in the same call. */
  onCreateLabel(name: string): void {
    const trimmed = name.trim();
    if (!trimmed) return;
    this.dispatchLabelUpdate({ create_and_apply: trimmed });
  }

  /** Closing the picker is what settles a visit, so the refetch happens here. */
  onLabelMenuClosed(): void {
    this.labelMenuIsOpen = false;

    if (!this.labelTimelineStale) return;
    this.labelTimelineStale = false;
    this.reloadThreadTimeline();
  }

  /**
   * Refetches the open thread so the timeline picks up the events the label
   * change wrote. One request per picker visit, however many labels it touched.
   */
  private reloadThreadTimeline(): void {
    const conversationId = this.activeConversationDetails?.id;
    if (conversationId == null) return;

    this.store.dispatch(smartInboxActions.LoadConversationDetails({ conversationId }));
  }

  private dispatchLabelUpdate(payload: IThreadLabelUpdate): void {
    const conversationId = this.activeConversationDetails?.id;
    if (conversationId == null) return;
    this.store.dispatch(smartInboxActions.UpdateThreadLabels({ conversationId, payload }));
  }

  // ── Provider labels awaiting a decision (IN-09) ────────────────────────────
  //
  // Decided here rather than only in settings because this is where a user
  // meets the label: the email arrives already wearing it. The settings queue
  // stays -- it is the way to work through a backlog -- but neither is the
  // other's shadow.

  /**
   * Details only. The list row carries no pending labels, so unlike
   * `conversationLabels` there is nothing to fall back to, and rendering an
   * empty row for a moment is correct rather than a flicker to hide.
   */
  get pendingProviderLabels(): IPendingProviderLabel[] {
    return this.activeConversationDetails?.pending_provider_labels ?? [];
  }

  get visiblePendingProviderLabels(): IPendingProviderLabel[] {
    return this.pendingProviderLabels.slice(0, MAX_VISIBLE_PENDING_LABELS);
  }

  get hiddenPendingProviderLabels(): IPendingProviderLabel[] {
    return this.pendingProviderLabels.slice(MAX_VISIBLE_PENDING_LABELS);
  }

  /** The ref a request is in flight for, so only its own chip shows busy. */
  decidingProviderRefId: number | null = null;

  trackByProviderRefId(_index: number, pending: IPendingProviderLabel): number {
    return pending.ref_id;
  }

  /**
   * Import turns the ref into a real label and backfills every conversation
   * already carrying it, so the thread is refetched rather than patched: the
   * chip must move from the dashed row to the solid one, and the timeline gains
   * the events the import wrote.
   */
  decidePendingProviderLabel(pending: IPendingProviderLabel, decision: 'accept' | 'reject'): void {
    if (this.decidingProviderRefId !== null) return;
    this.decidingProviderRefId = pending.ref_id;

    this.settingsService.decideProviderLabels([pending.ref_id], decision)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.decidingProviderRefId = null;
          this.commonService.showToast(
            this.translate.instant(
              decision === 'accept'
                ? 'smart_inbox.inbox_view.pending_labels.imported'
                : 'smart_inbox.inbox_view.pending_labels.ignored',
              { name: pending.name },
            ),
            'INFO',
            this.toastOptions,
          );
          this.reloadThreadTimeline();
        },
        error: () => {
          this.decidingProviderRefId = null;
        },
      });
  }
}
